import React, { useEffect, useState } from 'react';

import dayjs from 'dayjs';
import { Link } from 'react-router-dom';

import { WishlistButton } from '@pkg/components/Common/CardTile/CardBottom/WishlistButton';
import { useAgeString, useGetCurrencyConversion } from '@pkg/hooks';
import { convertEthToUsd } from '@pkg/utils';

import { CollectionChainType, SupportedTokens } from '@app/client';
import { useDaasGetCollectionTokens, useGetCollectionMetadata } from '@app/pages/Collection/hooks';
import { HoverUnderline } from '@app/pages/NFTDetailView/components/MainStage/components/NFTInfo/styles';
import { useBaseCurrencyStore, useExchangeRatesStore } from '@app/store/zustand';

import { Carousel } from '../Carousel';

import { ContentSkeleton } from './ContentSkeleton';
import { Detail } from './Detail';

import {
    CollectionThumbnail,
    DetailsContent,
    HeaderTextWrapper,
    HeaderWrapper,
    Heading,
    MainContent,
    SubHeading,
} from './styles';

interface ContentProps {
    slug?: string;
}

export const Content = ({ slug }: ContentProps) => {
    // We might want to render this in the "base currency".
    const [txVolumeUsd, setTxVolumeUsd] = useState<string>();

    const { data: tokenData, isLoading: tokenIsLoading } = useDaasGetCollectionTokens({
        slug,
        limit: 1,
        ignoreSignal: true,
    });

    const { data: collectionData } = useGetCollectionMetadata(slug);
    const { data: conversionData } = useGetCurrencyConversion({ interval: false });

    const {
        tokenCount,
        name,
        thumbnail,
        floorAsk,
        salesCount,
        percentListed,
        createdAt,
        volume,
        volumeChange,
        ownerCount,
    } = collectionData?.collection || {};

    const date = dayjs(createdAt).format('MMM YYYY');
    const { dateTimeAgo } = useAgeString(createdAt);

    const currency = useBaseCurrencyStore((state) => state.currency);
    const formatCurrency = useBaseCurrencyStore((state) => state.formatCurrency);
    const convertCurrency = useExchangeRatesStore((state) => state.convertCurrency);

    const converted = convertCurrency('usd', currency, floorAsk?.price?.amount?.usd);
    const floorPriceFormatted = formatCurrency(currency, converted, true);

    const imageUrls = tokenData?.pages[0]?.tokens?.map((item) => item?.image_url as string).filter((url) => !!url);

    const sales = salesCount && salesCount['1day'];
    const salesFormatted = sales
        ? parseFloat(sales).toLocaleString('en-US', {
              notation: 'compact',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
          })
        : '';

    // && tokenCount ? countFormatter(percentListed * tokenCount)?.toLocaleString() : '';
    const listed = (percentListed || 0) > 0 && `${((percentListed || 0) * 100).toFixed(2)}%`;

    useEffect(() => {
        const conversionRate = conversionData?.ethereum?.usd || 0;

        if (volume && volume['7day'] && conversionData) {
            const txVolume = convertEthToUsd(volume['7day'], conversionRate || 0);
            const txVolumeFormatted = txVolume.toLocaleString('en-US', {
                currency: 'USD',
                style: 'currency',
                notation: 'compact',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            });

            setTxVolumeUsd(txVolumeFormatted);
        }
    }, [conversionData, volume]);

    const txVolumeChange = (volumeChange?.['7day'] !== 0 && volumeChange?.['7day']) || undefined;

    const contentMarkup = collectionData ? (
        <MainContent>
            <HeaderWrapper>
                <CollectionThumbnail $image={thumbnail} />

                <HeaderTextWrapper>
                    <Link
                        to={`/collection/${SupportedTokens.Ethereum}/${slug}`}
                        onClick={(event) => event.stopPropagation()}
                    >
                        <Heading title={name}>
                            {name}
                            <HoverUnderline />
                        </Heading>
                    </Link>

                    <SubHeading>{tokenCount} Collectibles</SubHeading>
                </HeaderTextWrapper>

                <WishlistButton chain={CollectionChainType.Ethereum} collection_slug={slug} />
            </HeaderWrapper>

            <DetailsContent>
                <Detail topLabel="Floor Price" value={floorPriceFormatted} currency={currency} truncate />

                {listed && <Detail topLabel="Listed" value={listed} />}
                {dateTimeAgo && <Detail topLabel="Created at" value={date} />}

                {/*
                    <Detail topLabel="Players added" changePercent={99} value="74" bottomLabel="of 2,345" />
                */}

                {sales && sales !== '0' && <Detail topLabel="Sales" timeLabel="24 hrs" value={salesFormatted} />}

                {ownerCount && ownerCount > 0 && <Detail topLabel="Owners" value={ownerCount.toString()} />}

                {txVolumeUsd && (
                    <Detail topLabel="TX Volume" changePercent={txVolumeChange} timeLabel="7 day" value={txVolumeUsd} />
                )}
            </DetailsContent>
        </MainContent>
    ) : (
        <ContentSkeleton />
    );

    return (
        <>
            <Carousel imageUrls={imageUrls} isLoading={tokenIsLoading} />
            {contentMarkup}
        </>
    );
};
